import type {
	BandSetting,
	GnssSignalsSetting,
	VrsBandStats,
	VrsGnssStats,
} from './types/activeSignalsRecommendationsTypes';

// Порядок вызова с кнопки «Применить рекомендации»:
// canApplySignalRecommendations
//   -> applySignalRecommendations
//     -> gnssHasCheckedBand: ГНСС без включённого диапазона пропускается целиком
//     -> findBandStats: статистика одного диапазона
//     -> applyBandRecommendations: новый список сигналов этого диапазона
//       -> isCSignal, getNotRecommendedSignals
//       -> moveToFirstCSlot -> getFirstCIndex
//       -> signalsEqual

const NOT_RECOMMENDED_SIGNALS: Record<string, string[]> = {
	C: ['C7I', 'C7Q', 'C7X'],
};

// Код измерения C*. Фазовые L* в рекомендациях не участвуют.
const isCSignal = (signal: string) => signal.startsWith('C');

// Чёрный список только у BeiDou (код ГНСС C): C7I, C7Q, C7X.
export const getNotRecommendedSignals = (gnssCode: string) => NOT_RECOMMENDED_SIGNALS[gnssCode] || [];

// Статистика диапазона. Нет всей ГНСС — null, диапазон не трогаем.
// ГНСС есть, а диапазона нет — пустой список: выбранные C* считаются отсутствующими в измерениях.
const findBandStats = (
	vrsSignalsStats: VrsGnssStats[],
	gnssCode: string,
	bandKey: string,
): VrsBandStats | null => {
	const gnssStats = vrsSignalsStats.find(item => item.gnssCode === gnssCode);
	if (!gnssStats) {
		return null;
	}
	return gnssStats.bands.find(band => band.key === bandKey) ?? {
		key: bandKey,
		signals: [],
	};
};

// Индекс первого C*. Стоящие перед ним L* не сдвигаются.
const getFirstCIndex = (signals: string[]) => signals.findIndex(isCSignal);

// Ставит target на место текущего первого C*. Если C* в списке нет, дописывает в конец.
// Вызывается из applyBandRecommendations.
const moveToFirstCSlot = (signals: string[], target: string): string[] => {
	if (!signals.includes(target)) {
		return signals;
	}
	const without = signals.filter(signal => signal !== target);
	const firstCIndex = getFirstCIndex(without);
	if (firstCIndex === -1) {
		return [...without, target];
	}
	return [...without.slice(0, firstCIndex), target, ...without.slice(firstCIndex)];
};

// Сравнение с учётом порядка: тот же набор в другом порядке — это изменение.
const signalsEqual = (left: string[], right: string[]) => {
	if (left.length !== right.length) {
		return false;
	}
	return left.every((signal, index) => signal === right[index]);
};

// Один диапазон. null — список не менять.
// 1. Убрать выбранные C*, которых нет в статистике этого диапазона.
// 2. Убрать C7I/C7Q/C7X у BeiDou.
// 3. Дописать измеренные C* из каталога, которых нет в выборе: сначала больший count, при равенстве — по имени.
// 4. Включённый диапазон не опустошать, если в измерениях был хотя бы один C*.
// 5. Самый частый подходящий C* переставить в слот первого C* (moveToFirstCSlot).
const applyBandRecommendations = (
	gnssCode: string,
	band: BandSetting,
	bandStats: VrsBandStats,
): string[] | null => {
	const originalSelected = [...band.selectedSignals];
	const catalogSet = new Set(band.signals.map(signal => signal.value));
	const statsSet = new Set(bandStats.signals.map(item => item.signal));
	const notRecommended = new Set(getNotRecommendedSignals(gnssCode));

	let selected = [...originalSelected];

	selected = selected.filter(signal => !isCSignal(signal) || statsSet.has(signal));
	selected = selected.filter(signal => !notRecommended.has(signal));

	const selectedSet = new Set(selected);
	const toAppend = [...bandStats.signals]
		.filter(item => isCSignal(item.signal))
		.filter(item => !notRecommended.has(item.signal))
		.filter(item => catalogSet.has(item.signal))
		.filter(item => !selectedSet.has(item.signal))
		.sort((left, right) => {
			if (right.signals_count !== left.signals_count) {
				return right.signals_count - left.signals_count;
			}
			return left.signal.localeCompare(right.signal);
		})
		.map(item => item.signal);

	selected = [...selected, ...toAppend];

	const hasMeasuredC = bandStats.signals.some(item => isCSignal(item.signal));
	if (band.checked && selected.length === 0 && hasMeasuredC) {
		return null;
	}

	const statsForPrimary = bandStats.signals.filter(
		item => !notRecommended.has(item.signal) && catalogSet.has(item.signal),
	);
	if (statsForPrimary.length > 0) {
		const maxCount = Math.max(...statsForPrimary.map(item => item.signals_count));
		const tier = statsForPrimary
			.filter(item => item.signals_count === maxCount)
			.map(item => item.signal);
		const selectedCs = selected.filter(isCSignal);
		const firstC = selectedCs[0];
		if (!firstC || !tier.includes(firstC)) {
			let target: string | null = null;
			for (const signal of selected) {
				if (isCSignal(signal) && tier.includes(signal)) {
					target = signal;
					break;
				}
			}
			const primaryTarget = target ?? [...tier].sort((left, right) => left.localeCompare(right))[0];
			if (primaryTarget && (getFirstCIndex(selected) !== -1 || selected.includes(primaryTarget))) {
				selected = moveToFirstCSlot(selected, primaryTarget);
			}
		}
	}

	if (signalsEqual(selected, originalSelected)) {
		return null;
	}
	return selected;
};

// ГНСС обрабатывается, только если включён хотя бы один её диапазон.
const gnssHasCheckedBand = (gnssItem: GnssSignalsSetting) => gnssItem.bands.some(band => band.checked);

// Проход по форме. checked не меняет. changed — true, если изменился хотя бы один список сигналов.
export const applySignalRecommendations = (
	signalsSettings: GnssSignalsSetting[],
	vrsSignalsStats: VrsGnssStats[],
): { settings: GnssSignalsSetting[]; changed: boolean } => {
	let changed = false;
	const settings = signalsSettings.map(gnssItem => {
		if (!gnssHasCheckedBand(gnssItem)) {
			return gnssItem;
		}
		const bands = gnssItem.bands.map(band => {
			const bandStats = findBandStats(vrsSignalsStats, gnssItem.gnssCode, band.key);
			if (!bandStats) {
				return band;
			}
			const nextSelected = applyBandRecommendations(gnssItem.gnssCode, band, bandStats);
			if (nextSelected == null) {
				return band;
			}
			changed = true;
			return {
				...band,
				selectedSignals: nextSelected,
			};
		});
		return {
			...gnssItem,
			bands,
		};
	});
	return { settings, changed };
};

// Кнопка активна, только если applySignalRecommendations что-то изменит. Сам список не записывает.
export const canApplySignalRecommendations = (
	signalsSettings: GnssSignalsSetting[],
	vrsSignalsStats: VrsGnssStats[],
): boolean => applySignalRecommendations(signalsSettings, vrsSignalsStats).changed;
